/**
 * `createApiTenancySource` — the management-api tenancy port over the shared
 * `ApiClient`.
 *
 * Surfaces the caller's organizations and the tenants under the active org for
 * the Settings "Organizations & tenants" section + the top-bar switcher. The
 * wire is snake_case (`is_root`, `customer_id`, …); this adapter maps it to the
 * camelCase summaries the UI renders. Canonical paths (`/organizations`,
 * `/organizations/{id}/tenants`) are rewritten onto the `.management.` zone by
 * `RESOURCE_REWRITES`, so region routing / auth / envelope unwrap are inherited.
 *
 * Note: creating an *organization* is intentionally absent — `POST /organizations`
 * is the Auth0 first-login provisioning flow (it needs org/tenant/user ids minted
 * by the post-login Action), so it is not a dashboard-callable action.
 */
import { type ApiClient, unwrapData } from "@authe/fe-commons/api";
import { asNullableString, asRecord, asString, rowsFromWire } from "./wire";

export interface TenantSummary {
  id: string;
  name: string;
  slug: string | null;
  /** Root tenant of the org — cannot be deleted. */
  isRoot: boolean;
  /** Free-form descriptive label (`production`, `sandbox`, …); not a boundary. */
  environment: string | null;
  continent: string | null;
  status: string | null;
}

export interface OrganizationSummary {
  id: string;
  name: string;
  slug: string | null;
  isRoot: boolean;
  status: string | null;
  customerId: string | null;
}

/**
 * An organization the CALLER is a member of — the org-switcher feed.
 *
 * Distinct from {@link OrganizationSummary}, which is "organizations under my
 * customer" (an admin view). A user can be a member of orgs across customers —
 * that is exactly what accepting an invitation to a second org produces — so
 * these are different questions with different answers.
 */
export interface MyOrganization {
  orgId: string;
  name: string;
  slug: string | null;
  customerId: string | null;
  isRoot: boolean;
  /** The one the caller is signed into right now. */
  isCurrent: boolean;
}

export interface CreateTenantInput {
  name: string;
  environment?: string;
}

export interface TenancySource {
  /** Tenants under an organization (management-api `tenants:show`). */
  listTenants(orgId: string): Promise<TenantSummary[]>;
  /** Provision a new tenant under an org; returns the new tenant id. */
  createTenant(orgId: string, input: CreateTenantInput): Promise<{ id: string }>;
  /** Rename a tenant in place. */
  renameTenant(orgId: string, tenantId: string, name: string): Promise<void>;
  /** Delete a tenant (backend refuses the root + the caller's current tenant). */
  deleteTenant(orgId: string, tenantId: string): Promise<void>;
  /** Organizations visible to the caller's customer (management-api `organizations:list`). */
  listOrganizations(): Promise<OrganizationSummary[]>;
  /** Rename an organization (management-api `organizations:edit`). */
  renameOrganization(orgId: string, name: string): Promise<void>;
  /** Rename the customer (management-api `customers:edit`). */
  renameCustomer(customerId: string, name: string): Promise<void>;
  /**
   * Organizations the caller is a MEMBER of (permissions
   * `organizations:list`) — the org-switcher feed.
   */
  listMyOrganizations(): Promise<MyOrganization[]>;
  /**
   * Move the caller's home tenancy to another org they belong to (permissions
   * `organizations:personalize`).
   *
   * Has to be a server call: the post-login Action deliberately ignores
   * `/authorize` query params when resolving tenancy, so the front end cannot
   * switch org by asking Auth0 for one. The caller re-authenticates afterwards
   * to pick up the new home.
   */
  setActiveOrganization(orgId: string): Promise<void>;
  /**
   * Move the caller's active tenant within their current org (permissions
   * `tenants:list`).
   *
   * A server call for the same reason as {@link setActiveOrganization}: the
   * post-login Action reads the tenancy from `app_metadata` and ignores
   * `/authorize` params, so the selection has to be written there. The caller
   * re-authenticates afterwards to pick it up.
   */
  setActiveTenant(tenantId: string): Promise<void>;
}

function tenantFromWire(raw: Record<string, unknown>): TenantSummary {
  return {
    id: asString(raw.id ?? raw.tenant_id),
    name: asString(raw.name ?? raw.display_name),
    slug: asNullableString(raw.slug),
    isRoot: Boolean(raw.is_root ?? raw.is_root_tenant ?? false),
    environment: asNullableString(raw.environment),
    continent: asNullableString(raw.continent),
    status: asNullableString(raw.status),
  };
}

function myOrganizationFromWire(raw: Record<string, unknown>): MyOrganization {
  return {
    orgId: asString(raw.org_id ?? raw.id),
    name: asString(raw.name),
    slug: asNullableString(raw.slug),
    customerId: asNullableString(raw.customer_id),
    isRoot: Boolean(raw.is_root ?? false),
    isCurrent: Boolean(raw.is_current ?? false),
  };
}

function organizationFromWire(raw: Record<string, unknown>): OrganizationSummary {
  return {
    id: asString(raw.id ?? raw.org_id),
    name: asString(raw.name ?? raw.display_name),
    slug: asNullableString(raw.slug),
    isRoot: Boolean(raw.is_root ?? false),
    status: asNullableString(raw.status),
    customerId: asNullableString(raw.customer_id),
  };
}

export function createApiTenancySource(client: ApiClient): TenancySource {
  const tenantsPath = (orgId: string) =>
    `/organizations/${encodeURIComponent(orgId)}/tenants`;

  return {
    async listTenants(orgId: string): Promise<TenantSummary[]> {
      const res = await client.get<unknown>(tenantsPath(orgId));
      return rowsFromWire(unwrapData(res)).map(tenantFromWire);
    },

    async createTenant(orgId: string, input: CreateTenantInput): Promise<{ id: string }> {
      const body: Record<string, unknown> = { name: input.name };
      if (input.environment) body.environment = input.environment;
      const created = asRecord(unwrapData(await client.post<unknown>(tenantsPath(orgId), body)));
      client.invalidate(tenantsPath(orgId));
      return { id: asString(created.id ?? created.tenant_id) };
    },

    async renameTenant(orgId: string, tenantId: string, name: string): Promise<void> {
      await client.put<unknown>(
        `${tenantsPath(orgId)}/${encodeURIComponent(tenantId)}`,
        { name },
      );
      client.invalidate(tenantsPath(orgId));
    },

    async deleteTenant(orgId: string, tenantId: string): Promise<void> {
      await client.delete<void>(
        `${tenantsPath(orgId)}/${encodeURIComponent(tenantId)}`,
      );
      client.invalidate(tenantsPath(orgId));
    },

    async listOrganizations(): Promise<OrganizationSummary[]> {
      const res = await client.get<unknown>("/organizations");
      return rowsFromWire(unwrapData(res)).map(organizationFromWire);
    },

    async renameOrganization(orgId: string, name: string): Promise<void> {
      // Update-organization is a POST on management-api (operationId
      // `update-organization`, scope `organizations:edit`), not a PUT.
      await client.post<unknown>(
        `/organizations/${encodeURIComponent(orgId)}`,
        { name },
      );
      client.invalidate("/organizations");
    },

    async renameCustomer(customerId: string, name: string): Promise<void> {
      await client.put<unknown>(
        `/customers/${encodeURIComponent(customerId)}`,
        { name },
      );
    },

    async listMyOrganizations(): Promise<MyOrganization[]> {
      const res = await client.get<unknown>("/my-organizations");
      return rowsFromWire(unwrapData(res)).map(myOrganizationFromWire);
    },

    async setActiveOrganization(orgId: string): Promise<void> {
      await client.post<unknown>("/active-organization", { org_id: orgId });
      // The caller's tenancy just moved, so every cached response keyed on the
      // old org is stale. The re-auth that follows reloads the app anyway; this
      // keeps a caller that does not reload from reading the previous org.
      client.invalidate("/my-organizations");
    },

    async setActiveTenant(tenantId: string): Promise<void> {
      await client.post<unknown>("/active-tenant", { tenant_id: tenantId });
    },
  };
}
